/**
 * Scrubber inside Core: CMS universe validation, submission and review.
 * Laid out like the Issues Log: a title block, the pill tab group and the
 * primary action on one row, the member data reminder, then the work.
 */
import React, { useEffect, useMemo, useState } from 'react';
import ValidateView from './ValidateView';
import SubmissionsView from './SubmissionsView';
import AnalyticsView from './AnalyticsView';
import FdrView from './FdrView';
import PhiFriendlyNote from '../../components/PhiFriendlyNote.jsx';
import { TabBar } from './ui';
import { isComplianceUser, getJson } from './api';

const API_ROOT = process.env.REACT_APP_API_URL || '';

export default function ScrubberApp({ user }) {
  const compliance = isComplianceUser(user);
  const [view, setView] = useState('validate');
  const [resubmit, setResubmit] = useState(null);
  const [notice, setNotice] = useState('');
  const [unread, setUnread] = useState(0);
  const [pending, setPending] = useState(null);
  const [queue, setQueue] = useState(null);

  const tabs = useMemo(() => [
    ['validate', 'Validate'],
    ['submissions', compliance ? 'Review' : 'My submissions', typeof queue === 'number' ? queue : undefined],
    ...(compliance ? [['analytics', 'Analytics'], ['fdr', 'FDR vendors']] : []),
  ], [compliance, queue]);

  // Deep link from a universe request email or a POM follow up:
  // /app?app=scrubber&universe_request_id=<id>[&ack=1]
  // The recipient's door answers first (a department user holds no Universe
  // Requests module); compliance's own route is the fallback.
  useEffect(() => {
    const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    const id = params ? params.get('universe_request_id') : null;
    if (!id) return;
    const wantsAck = params.get('ack') === '1';
    const read = (path) => fetch(`${API_ROOT}${path}`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    // The door answers with its mode: 'recipient' (a named recipient, whose
    // statement pauses the reminders) or 'compliance' (a module holder, whose
    // statement marks the universes received). The compliance fallback route
    // carries no mode, so it is compliance by construction.
    read(`/api/universe-ack/request/${encodeURIComponent(id)}`)
      .then((d) => (d && d.request ? { ...d, mode: d.mode || 'recipient' } : read(`/api/universe-requests/${encodeURIComponent(id)}`).then((c) => (c && c.request ? { ...c, mode: 'compliance' } : null))))
      .then((d) => {
        const request = d && d.request;
        if (!request || !['sent', 'in_progress'].includes(request.status)) return;
        const items = (request.items || []).filter((i) => i.consumer_app === 'scrubber');
        if (!items.length) return;
        setPending({ request, items, wantsAck, mode: d.mode });
      })
      .catch(() => {});
  }, []);

  // The count on the Review tab: what awaits compliance, or what the user has
  // submitted. Notifications keep the unread count for the banner.
  useEffect(() => {
    let alive = true;
    const tick = () => {
      getJson(`/notifications?role=${compliance ? 'compliance_admin' : 'department_owner'}`).then((d) => { if (alive) setUnread(d.unreadCount || 0); }).catch(() => {});
      getJson(compliance ? '/submissions' : '/submissions?me=true').then((d) => {
        if (!alive) return;
        const list = d.submissions || [];
        setQueue(compliance ? list.filter((s) => s.status === 'submitted' || s.status === 'resubmitted').length : list.length);
      }).catch(() => {});
    };
    tick();
    const t = setInterval(tick, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [compliance]);

  return (
    <div className="min-h-screen bg-[#f8fafc] p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div>
            <h1 className="text-lg font-bold text-slate-800 tracking-tight">Scrubber</h1>
            <p className="text-slate-500 text-sm mt-0.5">CMS program audit universes, validated against the record layouts and submitted to compliance.</p>
          </div>
          <div className="flex items-center gap-3">
            <TabBar value={view} onChange={(v) => { setView(v); setNotice(''); }} tabs={tabs} />
            {unread > 0 && (
              <button type="button" onClick={() => setView('submissions')}
                className="px-3 py-1.5 rounded-full bg-orange-50 text-orange-700 text-xs font-semibold hover:bg-orange-100">
                {unread} update{unread === 1 ? '' : 's'}
              </button>
            )}
          </div>
        </div>

        <PhiFriendlyNote variant="ingest" className="mb-4" />

        {view === 'validate' && (
          <ValidateView key={resubmit ? `re-${resubmit.id}` : 'new'} user={user} resubmit={resubmit} pending={pending} onDismissPending={() => setPending(null)}
            onResubmitDone={() => { setResubmit(null); setNotice('Your corrected file was resubmitted. Compliance has been notified.'); setView('submissions'); }} />
        )}
        {view === 'submissions' && (
          <SubmissionsView user={user} compliance={compliance} notice={notice}
            onResubmit={(s) => { setResubmit(s); setView('validate'); }} />
        )}
        {view === 'analytics' && compliance && <AnalyticsView />}
        {view === 'fdr' && compliance && <FdrView />}
      </div>
    </div>
  );
}
